import * as Yup from "yup"
import { useEffect, useMemo, useState } from "react"
import { useFormik } from "formik"
import { useDispatch, useSelector } from "react-redux"

import { TStore } from "../../../../store/Store"
import {
    useGetCitiesQuery,
    useGetStatesQuery,
    useLazyGetCountryStatesQuery,
} from "../../../../pages/Common/commonService"
import { SitesObj, useAddSiteTicketMutation, useLazyGetAllSitesQuery } from "../../../../pages/Sites/SiteService"
import { EAccountRole } from "../../../../enums/commonEnums"
import {
    AccountsTableData,
    useLazyGetAccountMetricsQuery,
    useLazyGetAccountsQuery,
} from "../../../../pages/Accounts/AccountsServices"
import { DropdownObj } from "../../../../pages/Common/commonTypes"
import { EFormModule } from "../.."
import { setSitesFormDemolitionModule, setSitesFormModule } from "../../RaiseTicketFormSlice"

const validationSchema = (values: any, module: EFormModule) =>
    Yup.object({
        siteType:
            module === EFormModule.DEMOLITION || module === EFormModule.EV_APPLICATION
                ? Yup.mixed() // Skip validation
                : Yup.object({
                      id: Yup.number().required(),
                      title: Yup.string().required(),
                  }).required("Site Type is required"),

        siteName: values?.siteType?.title === "New" ? Yup.string().required("Site Name is required") : Yup.string(),

        siteAddress:
            values?.siteType?.title === "New" ? Yup.string().required("Site Address is required") : Yup.string(),
        pincode:
            values?.siteType?.title === "New"
                ? Yup.string()
                      .required("Pincode is required")
                      .matches(/^\d{6}$/, "Pincode must be a valid 6-digit number")
                : Yup.string(),
        country:
            values?.siteType?.title === "New"
                ? Yup.object({
                      id: Yup.number().required(),
                      title: Yup.string().required(),
                  }).required("Country is required")
                : Yup.mixed(),
        state:
            values?.siteType?.title === "New"
                ? Yup.object({
                      id: Yup.number().required(),
                      title: Yup.string().required(),
                  }).required("State is required")
                : Yup.mixed(),

        city:
            values?.siteType?.title === "New"
                ? Yup.object({
                      id: Yup.number().required(),
                      title: Yup.string().required(),
                  }).required("City is required")
                : Yup.mixed(),

        latitude: Yup.string()
            .required("Latitude is required")
            .matches(
                /^-?([1-8]?\d(\.\d{6,})?|90(\.0{6,})?)$/,
                "Please enter a numerical value between -90 to 90 with at least 6 digits after decimal."
            ),

        longitude: Yup.string()
            .required("Longitude is required")
            .matches(
                /^-?((1[0-7]\d|[1-9]?\d)(\.\d{6,})?|180(\.0{6,})?)$/,
                "Please enter a numerical value between -180 to 180 with at least 6 digits after decimal."
            ),
    })

export function useAccountLocation({ module, handleNextStep }: { module: EFormModule; handleNextStep: () => void }) {
    const dispatch = useDispatch()
    const {
        loginData: { user_data },
    } = useSelector((state: TStore) => state.verifyLogin)
    const [getCountryStates, { data: statesList }] = useLazyGetCountryStatesQuery()
    const { sites_form, sites_form_demolition } = useSelector((state: TStore) => state.RaiseTicketForm)

    const [addSiteTicket] = useAddSiteTicketMutation()
    const [getAccounts, { data: accountsApiData }] = useLazyGetAccountsQuery()
    const [getAccountMetrics] = useLazyGetAccountMetricsQuery()
    const [getAllSites] = useLazyGetAllSitesQuery()

    const [newlyAddedSite, setNewlyAddedSite] = useState<any>(null)
    const [accountsPage, setAccountsPage] = useState(1)
    const [nextClicked, setNextClicked] = useState(false)
    const [isAccountsLoading, setIsAccountsLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState("")
    const [accountsList, setAccountsList] = useState<any[]>([])
    const [totalPages, setTotalPages] = useState<number | null>(null)
    const [showAddAccountModal, setShowAddAccountModal] = useState(false)
    const [sitesList, setSitesList] = useState<SitesObj[]>([])
    const [sitesPage, setSitesPage] = useState(1)
    const [sitesTotalPages, setSitesTotalPages] = useState<number | null>(null)
    const [sitesSearchTerm, setSitesSearchTerm] = useState("")
    const [isSitesLoading, setIsSitesLoading] = useState(false)
    const [showDemolitionsErrors, setShowDemolitionsErrors] = useState(false)
    const [selectedAccountRoles, setSelectedAccountRoles] = useState<{ id: EAccountRole; title: EAccountRole }[]>(
        sites_form_demolition.selectedAccountRoles
    )
    const [sourceSite, setSourceSite] = useState<DropdownObj | null>(sites_form_demolition.sourceSite)
    const [destinationSite, setDestinationSite] = useState<DropdownObj | null>(sites_form_demolition.destinationSite)

    const [showEditAccountModal, setShowEditAccountModal] = useState<{
        isShown: boolean
        role: EAccountRole | null
    }>({
        isShown: false,
        role: null,
    })

    const [showSiteTable, setShowSiteTable] = useState<{
        isShown: boolean,
        type: "source" | "destination" | null,
    }>({
        isShown: false,
        type: null,
    })

    const [showDemolitionAddSites, setShowDemolitionAddSites] = useState(false)

    const [selectedAccountMap, setSelectedAccountMap] = useState<{
        [key in EAccountRole]?: AccountsTableData | null
    }>({
        [EAccountRole.BUYER]: null,
    })

    const [accountFormMap, setAccountFormMap] = useState<{
        [key in EAccountRole]?: {
            accountId: DropdownObj | null
            billingAddress: DropdownObj | null
            billingContact: DropdownObj | null
        }
    }>(sites_form_demolition.accountFormMap)

    const siteOptions = useMemo(() => {
        return sitesList.map(site => ({
            id: site.id,
            title: site.name,
        }))
    }, [sitesList])

    const accountRolesOptions = useMemo(
        () =>
            Object.values(EAccountRole).map(role => ({
                id: role,
                title: role,
                disabled: role === EAccountRole.BUYER,
            })),
        []
    )

    const formik = useFormik<any>({
        initialValues: sites_form,
        enableReinitialize: true,
        validationSchema: Yup.lazy(
            (values: any) => validationSchema(values, module) // Pass module into schema generator
        ),
        onSubmit: async values => {
            const data = {
                name: values.siteName,
                city_id: values.city?.id,
                state_id: values.state?.id,
                latitude: values.latitude,
                longitude: values.longitude,
                address: values.siteAddress,
                pincode: values.pincode,
                country_id: values?.country?.id,
            }
            try {
                const response = await addSiteTicket(data).unwrap()
                if (
                    showDemolitionAddSites &&
                    (module === EFormModule.DEMOLITION || module === EFormModule.EV_APPLICATION)
                ) {
                    setShowDemolitionAddSites(false)
                    fetchSites(1)
                } else {
                    setNewlyAddedSite(response)
                    formik.setFieldValue("siteType", { id: 0, title: "Existing" })
                }
            } catch (err) {
                console.error("Site creation failed", err)
            }
        },
    })

    const handleSelectAccount = (role: EAccountRole, accountId: number) => {
        const account = accountsApiData?.data.find(account => account.id === accountId)
        const accountDropdownValue = {
            id: account?.id,
            title: account?.account_name,
        }
        setSelectedAccountMap(prev => ({
            ...prev,
            [role]: account,
        }))
        setAccountFormMap(prev => ({
            ...prev,
            [role]: {
                billingAddress: null,
                billingContact: null,
                accountId: accountDropdownValue,
            },
        }))
    }

    const handleSelectBillingAddress = (role: EAccountRole, billingAddressId: number) => {
        const billingAddress = selectedAccountMap[role]?.billing_addresses?.find(
            address => address.id === billingAddressId
        )
        const val = billingAddressId
            ? {
                  id: billingAddress?.id,
                  title: `${billingAddress?.address}, ${billingAddress?.city}, ${billingAddress?.state}, ${billingAddress?.pincode}`,
              }
            : null
        setAccountFormMap(prev => ({
            ...prev,
            [role]: {
                ...prev[role],
                billingAddress: val,
            },
        }))
    }

    const handleSelectBillingContact = (role: EAccountRole, billingContactId: number) => {
        const billingContact = selectedAccountMap[role]?.account_contacts?.find(
            contact => contact.id === billingContactId
        )
        const val = billingContactId
            ? {
                  id: billingContact?.id,
                  title: `${billingContact?.name} - ${billingContact?.phone}`,
              }
            : null

        setAccountFormMap(prev => ({
            ...prev,
            [role]: {
                ...prev[role],
                billingContact: val,
            },
        }))
    }

    const handleRemoveAccountRole = (role: EAccountRole) => {
        setSelectedAccountRoles(selectedAccountRoles.filter(r => r.id !== role))
    }

    const handleChangeSelectType = (value: any) => {
        formik.setFieldValue("siteType", value)
        formik.setFieldValue("siteId", "")
        formik.setFieldValue("siteName", "")
        formik.setFieldValue("pincode", "")
        formik.setFieldValue("latitude", "")
        formik.setFieldValue("longitude", "")
        formik.setFieldValue("siteAddress", "")
        formik.setFieldValue("country", { id: 101, title: "India" })
        formik.setFieldValue("state", null)
        formik.setFieldValue("city", null)
    }
    const { data: citiesList } = useGetCitiesQuery(
        {
            state_id: [formik.values.state]?.map((state: any) => state?.id),
        },
        {
            skip: formik.values.state === null,
        }
    )

    const fetchSites = async (page: number) => {
        if (isSitesLoading) return
        if (sitesTotalPages !== null && page > sitesTotalPages) return
        setIsSitesLoading(true)
        try {
            const res = await getAllSites(
                {
                    page: page,
                    per_page: 20,
                    site: sitesSearchTerm,
                },
                true
            ).unwrap()

            if (res) {
                setSitesList(prev => [...prev, ...(res.data || [])])
                setSitesTotalPages(res.totalPages || 0)
            }
        } catch (error) {
            console.error("Error fetching sites:", error)
            setSitesList([])
            setSitesTotalPages(null)
            return
        } finally {
            setIsSitesLoading(false)
        }
    }

    const handleSearchSites = async (searchTerm: string) => {
        if (isSitesLoading) return
        setIsSitesLoading(true)
        setSitesSearchTerm(searchTerm)
        try {
            const res = await getAllSites(
                {
                    page: 1,
                    per_page: 20,
                    site: sitesSearchTerm,
                },
                true
            ).unwrap()
            if (res) {
                setSitesList(res.data || [])
                setSitesTotalPages(res.totalPages || 0)
            }
        } catch (error) {
            console.error("Error searching sites:", error)
            setSitesList([])
            setSitesTotalPages(null)
        } finally {
            setIsSitesLoading(false)
        }
    }

    const handleLoadMoreSites = () => {
        setSitesPage(prev => {
            const nextPage = prev + 1
            fetchSites(nextPage)
            return nextPage
        })
    }

    const fetchAccounts = async (page: number) => {
        if (isAccountsLoading) return
        if (totalPages !== null && page > totalPages) return
        setIsAccountsLoading(true)
        try {
            const res = await getAccounts(
                {
                    page: page,
                    per_page: 20,
                    search_key: searchTerm,
                    account_type: "Client",
                },
                true
            ).unwrap()

            const accountMetrics = await getAccountMetrics(
                {
                    per_page: 20,
                    search_key: searchTerm,
                    account_type: "Client",
                },
                true
            ).unwrap()

            const accounts = res.data.map(account => ({
                id: account.id,
                title: account.account_name,
            }))
            if (res) {
                setAccountsList(prev => [...prev, ...(accounts || [])])
                setTotalPages(accountMetrics.totalPages || 0)
            }
        } catch (error) {
            console.error("Error fetching accounts:", error)
            setAccountsList([])
            setTotalPages(null)
            return
        } finally {
            setIsAccountsLoading(false)
        }
    }

    const handleAccountSearch = async (searchTerm: string) => {
        if (isAccountsLoading) return
        setIsAccountsLoading(true)
        setSearchTerm(searchTerm)
        try {
            const res = await getAccounts(
                {
                    page: 1,
                    per_page: 20,
                    search_key: searchTerm,
                    account_type: "Client",
                },
                true
            ).unwrap()

            const accounts = res.data.map(account => ({
                id: account.id,
                title: account.account_name,
            }))

            const accountMetrics = await getAccountMetrics(
                {
                    per_page: 20,
                    search_key: searchTerm,
                    account_type: "Client",
                },
                true
            ).unwrap()
            if (res) {
                setAccountsList(accounts || [])
                setTotalPages(accountMetrics.totalPages || 0)
            }
        } catch (error) {
            console.error("Error searching sites:", error)
            setAccountsList([])
            setTotalPages(null)
        } finally {
            setIsAccountsLoading(false)
        }
    }

    const handleLoadMoreAccounts = () => {
        setAccountsPage(prev => {
            const nextPage = prev + 1
            fetchAccounts(nextPage)
            return nextPage
        })
    }

    const openEditAccountModal = (role: EAccountRole) => {
        setShowEditAccountModal({
            isShown: true,
            role: role,
        })
    }

    const closeEditAccountModal = () => {
        setShowEditAccountModal({
            isShown: false,
            role: null,
        })
    }

    const isDemolitionsFormValid = () => {
        selectedAccountRoles.forEach(role => {
            if (
                !accountFormMap[role.id]?.accountId ||
                !accountFormMap[role.id]?.billingAddress ||
                !accountFormMap[role.id]?.billingContact
            ) {
                return false
            }
        })
        if (
            !accountFormMap[EAccountRole.BUYER]?.accountId ||
            !accountFormMap[EAccountRole.BUYER]?.billingAddress ||
            !accountFormMap[EAccountRole.BUYER]?.billingContact
        ) {
            return false
        }
        if (!sourceSite || (!destinationSite && module !== EFormModule.EV_APPLICATION)) {
            return false
        }
        return true
    }

    const handleNext = () => {
        if (module === EFormModule.DEMOLITION || module === EFormModule.EV_APPLICATION || module === EFormModule.PROJECTS) {
            setShowDemolitionsErrors(true)
            if (isDemolitionsFormValid()) {
                const demolitionForm = {
                    accountFormMap: accountFormMap,
                    sourceSite: sourceSite,
                    destinationSite: destinationSite,
                    selectedAccountRoles: selectedAccountRoles,
                }
                dispatch(setSitesFormDemolitionModule(demolitionForm))
                handleNextStep()
            }
        } else {
            if (formik.values.siteId !== null && formik.values.siteId !== "") {
                setNextClicked(true)
                dispatch(setSitesFormModule({ ...formik.values, search_site_name: formik?.values?.siteName }))
                handleNextStep()
            }
        }
    }

    useEffect(() => {
        if (accountsList.length === 0) {
            fetchAccounts(1)
        }
    }, [accountsList.length])

    useEffect(() => {
        if (sitesList.length === 0) {
            fetchSites(1)
        }
    }, [sitesList.length])
    useEffect(() => {
        if (!formik.values.country?.id) {
            formik.setFieldValue("country", { id: 101, title: "India" })
        }
    }, [])
    useEffect(() => {
        if (formik?.values?.country?.id) {
            getCountryStates({ country_id: formik?.values?.country?.id })
        }
        formik.setFieldValue("state", null)
        formik.setFieldValue("city", null)
    }, [formik?.values?.country?.id])

    useEffect(() => {
        const { role_id } = user_data
        if (module === EFormModule.SURVEYS) return
        if (role_id !== 1 && role_id !== 51) {
            handleChangeSelectType({ id: 0, title: "Existing" })
        }
    }, [user_data?.role_id])

    return {
        formik,
        statesList,
        citiesList,
        newlyAddedSite,
        sites_form,
        setNewlyAddedSite,
        handleChangeSelectType,
        nextClicked,
        setNextClicked,
        showAddAccountModal,
        setShowAddAccountModal,
        accountRolesOptions,
        selectedAccountRoles,
        setSelectedAccountRoles,
        handleRemoveAccountRole,
        isAccountsLoading,
        getAccounts,
        accountFormMap,
        handleLoadMoreAccounts,
        setAccountFormMap,
        handleAccountSearch,
        accountsList,
        handleSelectAccount,
        selectedAccountMap,
        showEditAccountModal,
        openEditAccountModal,
        closeEditAccountModal,
        handleSelectBillingAddress,
        handleSelectBillingContact,
        showDemolitionAddSites,
        setShowDemolitionAddSites,
        siteOptions,
        isSitesLoading,
        handleSearchSites,
        handleLoadMoreSites,
        showDemolitionsErrors,
        handleNext,
        sourceSite,
        setSourceSite,
        destinationSite,
        setDestinationSite,
        showSiteTable,
        setShowSiteTable,
        user_data,
    }
}
